const {__} = window.wp.i18n;

/**
 * Load More Posts Module
 *
 * A reusable module for implementing "Load More" functionality with AJAX
 * on archive pages. Works with any post type and maintains SEO-friendliness
 * through progressive enhancement.
 *
 * Usage:
 * Add the following data attributes to your archive container:
 * - data-load-more-container: The container that holds posts
 * - data-load-more-current-page: Current page number
 * - data-load-more-max-pages: Maximum number of pages
 * - data-load-more-posts-selector: The selector of the container that contains the posts when loading them
 *
 * The module will:
 * 1. Hide the existing pagination
 * 2. Show a "Load More" button
 * 3. Load posts via AJAX
 * 4. Update the URL with pushState
 */

export default function loadMorePosts() {
  const containers = document.querySelectorAll('[data-load-more-container]');

  if (containers.length === 0) {
    return;
  }

  containers.forEach(container => {
    initLoadMore(container);
  });
}

function initLoadMore(container) {
  const config = getConfig(container);

  if (!config) {
    return;
  }

  hidePagination(container);

  const loadMoreBtn = createLoadMoreButton(config);
  const postsContainer = container.querySelector(config.postsSelector);

  if (!postsContainer) {
    console.warn('Load More: Posts container not found with selector:', config.postsSelector);

    return;
  }

  postsContainer.insertAdjacentElement('afterend', loadMoreBtn);

  const button = loadMoreBtn.querySelector('.load-more-btn');

  button.addEventListener('click', () => {
    handleLoadMore(button, loadMoreBtn, postsContainer, config);
  });

  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.page) {
      window.location.reload();
    }
  });
}

function getConfig(container) {
  const maxPages = parseInt(container.dataset.loadMoreMaxPages, 10);
  const postsSelector = container.dataset.loadMorePostsSelector || '.grid-1-2-3';
  let currentPage = parseInt(container.dataset.loadMoreCurrentPage, 10);

  if (!maxPages || !currentPage) {
    console.warn('Load More: Missing required data attributes (current-page, max-pages)');

    return null;
  }

  return {
    currentPage,
    maxPages,
    postsSelector,
    container
  };
}

function hidePagination(container) {
  const pagination = container.querySelector('.wp-block-query-pagination');
  if (pagination) {
    // Use visibility + positioning instead of display:none for better SEO
    // This keeps the links in the DOM and crawlable, but hides them visually
    pagination.style.position = 'absolute';
    pagination.style.left = '-9999px';
    pagination.style.visibility = 'hidden';
    pagination.setAttribute('aria-hidden', 'true');
  }
}

function createLoadMoreButton(config) {
  const wrapper = document.createElement('div');
  wrapper.className = 'load-more-wrapper';
  wrapper.style.cssText = 'text-align: center; margin: 2rem 0;';

  const button = document.createElement('button');
  button.className = 'btn btn-primary load-more-btn';
  button.type = 'button';
  button.textContent = getLoadMoreText();

  if (config.currentPage >= config.maxPages) {
    wrapper.style.display = 'none';
  }

  wrapper.appendChild(button);

  return wrapper;
}

function getLoadMoreText() {
  if (window.wp && window.wp.i18n) {
    return __('Load More', 'ae');
  }

  return 'Load More';
}

function getLoadingText() {
  if (window.wp && window.wp.i18n) {
    return __('Loading...', 'ae');
  }

  return 'Loading...';
}

async function handleLoadMore(button, buttonWrapper, postsContainer, config) {
  const nextPage = config.currentPage + 1;

  if (nextPage > config.maxPages) {
    buttonWrapper.style.display = 'none';

    return;
  }

  const originalText = button.textContent;

  button.disabled = true;
  button.textContent = getLoadingText();

  try {
    const data = await loadPosts(nextPage, config);

    if (data.success && data.data.html) {
      // Create a temporary container to parse the HTML
      const temp = document.createElement('div');

      temp.innerHTML = data.data.html;

      const newPosts = temp.children;

      Array.from(newPosts).forEach(post => {
        postsContainer.appendChild(post);
      });

      config.currentPage = nextPage;
      config.container.dataset.loadMoreCurrentPage = nextPage;

      if (!data.data.has_more || nextPage >= config.maxPages) {
        buttonWrapper.style.display = 'none';
      } else {
        button.disabled = false;
        button.textContent = originalText;
      }
    } else {
      throw new Error(data.data?.message || 'Failed to load posts');
    }
  } catch(error) {
    console.error('Load More: Error loading posts', error);

    button.disabled = false;
    button.textContent = originalText;

    showErrorMessage(buttonWrapper, error.message);
  }
}

async function loadPosts(page, config) {
  const url = new URL(window.location);

  url.searchParams.set('paged', page);

  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'same-origin',
    headers: {'X-Requested-With': 'XMLHttpRequest'}
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const html = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const postsContainer = doc.querySelector(config.postsSelector);

  if (!postsContainer) {
    throw new Error('Posts container not found in loaded page');
  }

  const pagination = doc.querySelector('.wp-block-query-pagination');
  const hasNextLink = pagination?.querySelector('.wp-block-query-pagination-next');

  return {
    success: true,
    data: {
      html: postsContainer.innerHTML,
      has_more: !!hasNextLink,
      current_page: page
    }
  };
}

function showErrorMessage(buttonWrapper, message) {
  const errorDiv = document.createElement('div');
  errorDiv.className = 'load-more-error';
  errorDiv.style.cssText = 'color: #d63638; margin-top: 1rem; text-align: center;';
  errorDiv.textContent = message || 'An error occurred while loading posts.';

  buttonWrapper.appendChild(errorDiv);

  setTimeout(() => {
    errorDiv.remove();
  }, 5000);
}

// Make available globally for WordPress enqueue
window.loadMorePosts = loadMorePosts;

